import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import Button from '@mui/material/Button';

import TextField from "@mui/material/TextField";

import useHttp from "../../../hooks/useHttp";
import "../../../assets/css/utility/input-field.css";
import "../../../assets/css/user_auth_pages.css";
import Styles from "./AccountVerification.module.css";
import Loader from "../../../components/Loader";
import SuccessMessage from "../../../components/SuccessMessage";
import { RESEND_VERIFICATION_CODE_URL, VERIFY_ACCOUNT_URL } from "../../../utils/api-ends";
import { fetchBasicInfo } from "../../../store/slices/basicInfo";

const AccountVerification = () => {
  const verifyCountDownRef = useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [verificationKey, setVerificationKey] = useState("");
  const [verificationKeyError, setVerificationKeyError] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [verifyBy, setVerifyBy] = useState("");
  const [timeCountDown, setTimeCountDown] = useState(60);
  const [successMessage, setSuccessMessage] = useState("");

  const { error, isLoading, sendRequests } = useHttp();
  const {
    error: resendError,
    isLoading: resendIsLoading,
    sendRequests: resendRequests,
  } = useHttp();

  const toVerify = searchParams.get("to_verify") ? searchParams.get("to_verify") : 'email';

  const onVerificationKeyChange = (e) => setVerificationKey(e.target.value);

  const onSubmit = (e) => {
    // Clear error messages before submitting form data
    setVerificationKeyError([]);

    if (verificationKey.length === 0) {
      setVerificationKeyError(["Verification key is required"]);
      return;
    }

    const response = (data) => {
      setSuccessMessage(data.message);

      // Assign user info to store
      data.is_complete_profile ? dispatch(fetchBasicInfo({})) : dispatch(fetchBasicInfo({isAuthenticated: true,}));
      
      // Navigate to home page
      setTimeout(() => {
        data.is_complete_profile ? navigate("/profile-information/") : navigate("/complete-user-profile")
      }, 3000);
    };

    sendRequests(
      {
        method: "post",
        url: VERIFY_ACCOUNT_URL,
        data: {
          verification_key: verificationKey,
          to_verify: verifyBy,
        },
      },
      response
    );
  };

  useEffect(() => {
    if (toVerify === "email" || toVerify === "phone") {
      setVerifyBy(toVerify);
    } else if (toVerify === "both") {
      setVerifyBy("email");
    }
  }, [toVerify]);

  const handleVerifyBy = () => {
    if (verifyBy === "email") {
      setVerifyBy("phone");
    } else if (verifyBy === "phone") {
      setVerifyBy("email");
    }
  };

  // Resend verification code count down
  const handleResend = () => {
    if (timeCountDown === 0 || timeCountDown === 60) {
      const response = (data) => {
        // Count down timer for resend verification code
        verifyCountDownRef.current = setInterval(() => {
          setTimeCountDown((prevS) => prevS - 1);
        }, 1000);
      };

      // Send request to resend verification code
      resendRequests(
        {
          method: "post",
          url: RESEND_VERIFICATION_CODE_URL,
          data: {
            to_verify: verifyBy,
          }
        },
        response
      );
    }
  };

  // Clear resend verification code count down
  useEffect(() => {
    if (timeCountDown === 0) {
      clearInterval(verifyCountDownRef.current);
      setTimeCountDown(60);
    }
  }, [timeCountDown]);

  const logout = (e) => {
    // Assign user info to store
    dispatch(
      fetchBasicInfo({
        isAuthenticated: false,
      })
    );

    localStorage.removeItem("auth_token");
    window.location.href = `/`;
  };

  return (
    <>
      <p className="auth-instruction">
        {toVerify === "email" &&
          " We have sent you an email to verify your account, please check your email (inbox/spam)."}
        {toVerify === "phone" &&
          " We have sent a verification code to your phone, please check it"}

        {toVerify === "both" &&
          (verifyBy === "email" ? (
            <>
              <br />
              <span>
                We have sent you an email to verify your account, please check
                your email (inbox/spam). If you want to verify your account
                using phone number, please click on the link below.
              </span>
            </>
          ) : (
            <>
              <br />
              <span>
                We have sent a verification code to your phone, please check it.
                If you want to verify your account using email, please click on
                the link below.
              </span>
            </>
          ))}

        
      </p>

      <div className="user-input-field">
        {successMessage && <SuccessMessage message={successMessage} />}
      </div>

      <div className="user-input-field">
        {error &&
          Object.keys(error).map(function (key) {
            return (
              <p className="auth-error" key={key}>
                {error[key]}
              </p>
            );
          })}

        {resendError &&
          Object.keys(resendError).map(function (key) {
            return (
              <p className="auth-error" key={key}>
                {resendError[key]}
              </p>
            );
          })}

        <div className="input-box-area">
          {verificationKeyError?.map((error, index) => (
            <p className="auth-error" key={index}>
              {error}
            </p>
          ))}
          <TextField
            fullWidth
            className="the-mui-input-field"
            onChange={onVerificationKeyChange}
            label="Verification Code"
            variant="outlined"
          />
        </div>
        <Button className="theme-btn submit-btn" onClick={onSubmit}>
          Submit
          {isLoading && <Loader style={{ marginLeft: "20px" }} />}
        </Button>
      </div>

      {toVerify === "both" &&
        (verifyBy === "email" ? (
          <a href="#/" onClick={handleVerifyBy}>
            Want to verify by phone ?
          </a>
        ) : (
          <a href="#/" onClick={handleVerifyBy}>
            Want to verify by email ?
          </a>
        ))}

      

      <div className={Styles.ResetCodeArea}>
        <a href="#/" onClick={handleResend}>
          Resend verification code to my {verifyBy}
        </a>
        <p className={Styles.CountDownTime}>
          {timeCountDown > 0 && timeCountDown < 60 && timeCountDown}
        </p>

        {resendIsLoading && <Loader style={{ marginLeft: "10px" }} />}
      </div>

      <br />

      <a href="#/" onClick={logout}>Logout</a>
    </>
  );
};

export default AccountVerification;
